import React, {forwardRef, useContext} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';

import Select from 'components/common/Form/Select';
import Input from 'components/common/Form/Input';
import LanguageSelect from 'components/common/Form/LanguageSelect';

import {newsChannels, tvBrands, phoneBrands, phoneModels, carMakes, carsModels} from 'constants/BrandsInformation';
import {isRespondent} from 'constants/UserRoles';

import {UserContext} from 'context/UserContext';

import style from 'components/ProfileEdit/InterviewEditTab/InterviewTab/InterviewTab.module.scss';
import cn from 'classnames';

const ConsumerInfo = ({id, values, errors, onChangeField, containerRef, isModal}) => {
    const {t} = useTranslation();
    const {
        user: {role},
    } = useContext(UserContext);

    const onChangePhoneBrandField = e => {
        onChangeField(e);
        onChangeField({target: {name: 'smartphoneModel', value: null}});
    };

    const onCarChange = e => {
        onChangeField(e);
        onChangeField({target: {name: 'carModel', value: null}});
    };

    const displayTooltips = isRespondent(role);

    return (
        <div id={id} ref={containerRef}>
            <div className={cn({[style.grid]: !isModal})}>
                {!isModal && <span className={style.title}>{t(`profile:text.consumerInformation`)}</span>}
                <div className={style.col}>
                    <div className={cn(style.threeGrid, {[style.modal]: isModal})}>
                        <div className={style.col}>
                            <Select
                                fullWidth
                                value={values.smartphoneBrand}
                                onChangeField={onChangePhoneBrandField}
                                error={errors.smartphoneBrand}
                                options={phoneBrands}
                                label={t(`profile:text.phoneBrandLabel`)}
                                placeholder={t('profile:text.select')}
                                name='smartphoneBrand'
                                customTooltipText={displayTooltips && t('tooltips:phoneBrand')}
                            />
                        </div>
                        <div className={style.col}>
                            <Select
                                fullWidth
                                label={t(`profile:text.model`)}
                                placeholder={t('profile:text.select')}
                                name='smartphoneModel'
                                value={values.smartphoneModel}
                                error={errors.smartphoneModel}
                                onChangeField={onChangeField}
                                options={phoneModels[values.smartphoneBrand] || []}
                                className={style.select}
                                customTooltipText={displayTooltips && t('tooltips:phoneModel')}
                            />
                        </div>
                        <div className={style.col}>
                            <Input
                                fullWidth
                                label={t(`profile:text.colorEdit`)}
                                name='smartphoneColor'
                                value={values.smartphoneColor}
                                error={errors.smartphoneColor}
                                onChangeField={onChangeField}
                                customTooltipText={displayTooltips && t('tooltips:phoneColour')}
                            />
                        </div>

                        <div className={cn(style.col, style.half)}>
                            <Select
                                fullWidth
                                value={values.tvBrand}
                                onChangeField={onChangeField}
                                error={errors.tvBrand}
                                options={tvBrands}
                                label={t(`profile:text.tvBrandLabel`)}
                                placeholder={t('profile:text.select')}
                                name='tvBrand'
                                customTooltipText={displayTooltips && t('tooltips:tvBrand')}
                            />
                        </div>
                        <div className={cn(style.col, style.half)}>
                            <Input
                                fullWidth
                                label={t(`profile:text.tvModelEdit`)}
                                name='tvModel'
                                value={values.tvModel}
                                error={errors.tvModel}
                                onChangeField={onChangeField}
                                customTooltipText={displayTooltips && t('tooltips:tvModel')}
                            />
                        </div>
                        <div />
                        <div className={style.col}>
                            <Select
                                fullWidth
                                label={t(`profile:text.carMake`)}
                                placeholder={t('profile:text.select')}
                                name='cars'
                                value={values.cars}
                                error={errors.cars}
                                onChangeField={onCarChange}
                                customTooltipText={displayTooltips && t('tooltips:carMake')}
                                options={carMakes}
                            />
                        </div>
                        <div className={style.col}>
                            <Select
                                fullWidth
                                label={t(`profile:text.carModel`)}
                                placeholder={t('profile:text.select')}
                                name='carModel'
                                value={values.carModel}
                                error={errors.carModel}
                                onChangeField={onChangeField}
                                customTooltipText={displayTooltips && t('tooltips:carModel')}
                                options={carsModels[values.cars] || []}
                            />
                        </div>
                        <div />
                        <div className={style.col}>
                            <Input
                                fullWidth
                                label={t(`profile:text.brandsEdit`)}
                                name='brands'
                                value={values.brands}
                                error={errors.brands}
                                onChangeField={onChangeField}
                            />
                        </div>
                        <div className={style.col}>
                            <Input
                                fullWidth
                                label={t(`profile:text.tvShowsEdit`)}
                                name='tvShows'
                                value={values.tvShows}
                                error={errors.tvShows}
                                onChangeField={onChangeField}
                            />
                        </div>
                    </div>
                    <div className={cn(style.row, style.topMargin)}>
                        <LanguageSelect
                            getName={x => x}
                            selected={values.newsChannels || []}
                            label={t(`profile:text.newsChannelsEdit`)}
                            name='newsChannels'
                            error={errors.newsChannels}
                            onChangeField={onChangeField}
                            options={newsChannels
                                .filter(key => !values.newsChannels.includes(key))
                                .map(key => ({label: key, value: key}))}
                            customTooltipText={displayTooltips && t('tooltips:newsChannels')}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

ConsumerInfo.propTypes = {
    id: PropTypes.string,
    values: PropTypes.object,
    errors: PropTypes.object,
    onChangeField: PropTypes.func,
};

ConsumerInfo.defaultProps = {
    id: null,
    values: {},
    errors: {},
    onChangeField: f => f,
};

export default forwardRef((props, ref) => <ConsumerInfo {...props} containerRef={ref} />);
